import {
  AnchorButton,
  ButtonGroup,
  Icon,
  IconName,
  InputGroupProps,
  MaybeElement,
  PopoverPosition,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Popover2Props } from '@blueprintjs/popover2';
import {
  ItemListRenderer,
  ItemPredicate,
  ItemsEqualProp,
  Select2,
  Select2Props,
} from '@blueprintjs/select';
import classNames from 'classnames';
import * as React from 'react';
import { FixedSizeList } from 'react-window';

import { NoResultsMenuItem } from 'bones/components/atoms/NoResultsMenuItem';
import { Text } from 'bones/components/atoms/Text';
import { Tooltip, TooltipProps } from 'bones/components/atoms/Tooltip';
import { createNewSelectItemRenderer } from 'bones/components/molecules/ItemSelector/renderers/createNewSelectItemRenderer';
import { loadingItemListRenderer } from 'bones/components/molecules/ItemSelector/renderers/loadingItemListRenderer';
import {
  SelectItemRendererOptions,
  isItemSelected,
  selectItemRenderer,
} from 'bones/components/molecules/ItemSelector/renderers/selectItemRenderer';
import { virtualizedSelectItemListRenderer } from 'bones/components/molecules/ItemSelector/renderers/virtualizedSelectItemListRenderer';
import { isEqual } from 'bones/utils/isEqual';
import { createNewSelectItemFromQuery } from 'bones/utils/select/createNewSelectItemFromQuery';
import {
  SELECT_ITEM_DIVIDER_VALUE,
  createSelectItemDivider,
} from 'bones/utils/select/createSelectItemDivider';
import { selectItemListPredicate } from 'bones/utils/select/selectItemListPredicate';
import { selectItemPredicate } from 'bones/utils/select/selectItemPredicate';
import { BaseSelectItem, SelectItem } from 'bones/utils/select/types';

export interface ItemSelectorProps<
  SubItemType extends SelectItem | null,
  ItemType extends SelectItem<SubItemType>,
  SelectionType extends
    | SubItemType
    | ItemType
    | SubItemType[]
    | ItemType[]
    | null
> extends SelectItemRendererOptions<SubItemType, ItemType, SelectionType> {
  // required
  buttonText: React.ReactNode;
  items: ItemType[];
  onSelect: (value: SelectionType, event?: React.MouseEvent) => any;

  // optional
  buttonClassName?: string;
  buttonIcon?: IconName | MaybeElement;
  buttonIconColor?: string;
  buttonMinimal?: boolean;
  buttonOutlined?: boolean;
  buttonRightIcon?: IconName | MaybeElement;
  className?: string;
  clearable?: boolean;
  clearTestId?: string;
  creatable?: boolean;
  createNewItemFromQuery?: Select2Props<ItemType>['createNewItemFromQuery'];
  createNewItemRenderer?: (
    items: ItemType[],
    testId?: string
  ) => Select2Props<ItemType>['createNewItemRenderer'];
  createNewItemTestId?: string;
  createText?: (query: string) => string;
  disabled?: boolean;
  filterable?: boolean;
  filterInputProps?: InputGroupProps;
  filterOrCreateText?: string;
  filterTestId?: string;
  filterText?: string;
  isLoadingItems?: boolean;
  itemPredicate?: (
    items: Array<SelectItem<SubItemType>>
  ) => ItemPredicate<SelectItem<SubItemType>>;
  itemsEqual?: ItemsEqualProp<ItemType>;
  keepOpenOnSelect?: boolean;
  loadingTestId?: string;
  noResults?: string;
  onButtonClick?: (event: React.MouseEvent) => any;
  onClear?: (event: React.MouseEvent) => any;
  onQueryChange?: (query: string) => any;
  popoverProps?: Popover2Props;
  resetOnClose?: Select2Props<ItemType>['resetOnClose'];
  /** sort selected items to the top of the list */
  sortSelectedFirst?: boolean;
  testId?: string;
  tooltipProps?: TooltipProps;
  virtualized?: boolean;
}

interface ItemSelectorState<
  SubItemType extends SelectItem | null,
  ItemType extends SelectItem<SubItemType>
> {
  isOpen: boolean;
  isSelectorHovered: boolean;
  prevItems: ItemType[];
  sortedItems: ItemType[];
}

export type SingleItemSelector<
  SubItemType extends SelectItem | null = null,
  ItemType extends SelectItem<SubItemType> = SelectItem<SubItemType>
> = ItemSelector<SubItemType, ItemType, ItemType | null>;

export type MultiItemSelector<
  SubItemType extends SelectItem | null = null,
  ItemType extends SelectItem<SubItemType> = SelectItem<SubItemType>
> = ItemSelector<SubItemType, ItemType, ItemType[]>;

export class ItemSelector<
  SubItemType extends SelectItem | null = null,
  ItemType extends SelectItem<SubItemType> = SelectItem<SubItemType>,
  SelectionType extends
    | SubItemType
    | ItemType
    | null
    | SubItemType[]
    | ItemType[] = ItemType[]
> extends React.Component<
  ItemSelectorProps<SubItemType, ItemType, SelectionType>,
  ItemSelectorState<SubItemType, ItemType>
> {
  public static Single<
    T extends BaseSelectItem | null = null,
    U extends SelectItem<T> = SelectItem<T>,
    S extends T | U | null = T | U | null
  >({
    selectorRef,
    ...props
  }: ItemSelectorProps<T, U, S> & {
    selectorRef?: React.Ref<ItemSelector<T, U, S>>;
  }) {
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <ItemSelector<T, U, S> ref={selectorRef} {...props} />;
  }

  public static Multi = <
    T extends BaseSelectItem | null = null,
    U extends SelectItem<T> = SelectItem<T>,
    S extends T[] | U[] = T[] | U[]
  >({
    selectorRef,
    ...props
  }: ItemSelectorProps<T, U, S> & {
    selectorRef?: React.Ref<ItemSelector<T, U, S>>;
  }) => {
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <ItemSelector<T, U, S> ref={selectorRef} {...props} />;
  };

  static getSortedItems<
    SubItemType extends SelectItem | null = null,
    ItemType extends SelectItem<SubItemType> = SelectItem<SubItemType>,
    SelectionType extends
      | SubItemType
      | ItemType
      | null
      | SubItemType[]
      | ItemType[] = ItemType[]
  >(props: ItemSelectorProps<SubItemType, ItemType, SelectionType>) {
    const { items, selected, sortSelectedFirst } = props;

    if (!sortSelectedFirst) {
      return items;
    }

    const itemsSelected: ItemType[] = [];
    const itemsNotSelected: ItemType[] = [];
    items.forEach((item) => {
      if (isItemSelected(item, selected)) {
        itemsSelected.push(item);
      } else {
        itemsNotSelected.push(item);
      }
    });
    if (itemsSelected.length) {
      const itemsNotSelectedWithoutUselessDividers = itemsNotSelected.filter(
        (item, i) =>
          item.value !== SELECT_ITEM_DIVIDER_VALUE ||
          (itemsNotSelected[i + 1] &&
            itemsNotSelected[i + 1].value !== SELECT_ITEM_DIVIDER_VALUE)
      );
      return [
        ...itemsSelected,
        createSelectItemDivider() as ItemType,
        ...itemsNotSelectedWithoutUselessDividers,
      ];
    }
    return items;
  }

  static getDerivedStateFromProps<
    SubItemType extends SelectItem | null = null,
    ItemType extends SelectItem<SubItemType> = SelectItem<SubItemType>,
    SelectionType extends
      | SubItemType
      | ItemType
      | null
      | SubItemType[]
      | ItemType[] = ItemType[]
  >(
    props: Readonly<ItemSelectorProps<SubItemType, ItemType, SelectionType>>,
    state: ItemSelectorState<SubItemType, ItemType>
  ): Partial<ItemSelectorState<SubItemType, ItemType>> | null {
    const { items } = props;
    const { prevItems } = state;

    if (!isEqual(items, prevItems)) {
      return {
        sortedItems: ItemSelector.getSortedItems(props),
        prevItems: items,
      };
    }
    return null;
  }

  listRef: React.MutableRefObject<FixedSizeList | null> = React.createRef();

  constructor(props: ItemSelectorProps<SubItemType, ItemType, SelectionType>) {
    super(props);

    this.state = {
      isOpen: false,
      isSelectorHovered: false,
      prevItems: [],
      sortedItems: [],
    };
  }

  handleButtonClick = (event: React.MouseEvent) => {
    this.setState({
      isOpen: true,
      sortedItems: ItemSelector.getSortedItems(this.props),
    });
    const { onButtonClick } = this.props;
    onButtonClick?.(event);
  };

  handleMouseEnter = () => {
    this.setState({ isSelectorHovered: true });
  };

  handleMouseLeave = () => {
    this.setState({ isSelectorHovered: false });
  };

  handleSelect = (
    maybeItem: ItemType | NonNullable<SubItemType>,
    evt?: React.SyntheticEvent<HTMLElement>
  ) => {
    if (maybeItem.value === SELECT_ITEM_DIVIDER_VALUE) {
      return;
    }

    let item: ItemType | NonNullable<SubItemType> = maybeItem;
    // eslint-disable-next-line prefer-destructuring
    const items: SubItemType[] | undefined = item.items;
    if (items) {
      const firstItem =
        item.collapseSingleItem && items.length === 1 && items[0];
      if (firstItem) {
        // select the first and only sub-item instead of selecting the sub-item parent
        item = firstItem;
      } else {
        // otherwise, do not allow selecting an item that contains sub-items
        return;
      }
    }

    const event = evt as React.MouseEvent<HTMLElement> | undefined;
    const { keepOpenOnSelect, selected, onSelect } = this.props;
    if (Array.isArray(selected)) {
      const newSelected = [...selected];
      if (event?.shiftKey) {
        const foundIndex = newSelected.findIndex(
          (selectedItem) => selectedItem.value === item.value
        );
        if (foundIndex > -1) {
          newSelected.splice(foundIndex, 1);
        } else {
          newSelected.push(item);
        }
      } else {
        newSelected.length = 0;
        newSelected.push(item);
        if (!keepOpenOnSelect) {
          this.setState({ isOpen: false });
        }
      }
      onSelect(newSelected as SelectionType, event);
    } else {
      if (!keepOpenOnSelect) {
        this.setState({ isOpen: false });
      }
      onSelect(item as SelectionType, event);
    }
  };

  handleClear = (event: React.MouseEvent) => {
    const { selected, onSelect, onClear } = this.props;
    const selection = (Array.isArray(selected) ? [] : null) as SelectionType;
    onSelect(selection);
    onClear?.(event);
  };

  render() {
    const {
      buttonClassName,
      buttonIcon,
      buttonIconColor,
      buttonMinimal,
      buttonOutlined,
      buttonRightIcon = IconNames.CARET_DOWN,
      buttonText,
      className,
      clearable,
      clearTestId,
      creatable,
      createNewItemFromQuery = createNewSelectItemFromQuery,
      createNewItemTestId,
      createText,
      disabled,
      filterable,
      filterInputProps,
      filterOrCreateText,
      filterTestId,
      filterText,
      isLoadingItems,
      itemClassName,
      itemMultiline,
      items,
      itemsEqual,
      itemTestId,
      loadingTestId,
      noResults,
      onQueryChange,
      popoverProps,
      resetOnClose,
      selected,
      subItemTestId,
      testId,
      tooltipProps,
      virtualized,

      createNewItemRenderer = createNewSelectItemRenderer<
        SubItemType,
        ItemType
      >({ createText }),
      itemPredicate = selectItemPredicate<SubItemType>(),
    } = this.props;

    const { isOpen, isSelectorHovered, sortedItems } = this.state;

    const noResultsMenuItem = <NoResultsMenuItem text={noResults} />;

    const itemRenderer = selectItemRenderer<
      SubItemType,
      ItemType,
      SelectionType
    >({
      itemClassName,
      itemMultiline,
      itemTestId,
      subItemTestId,
      selected,
      handleSelect: this.handleSelect,
    });

    const itemListPredicate = selectItemListPredicate<SubItemType, ItemType>({
      itemPredicate,
    });

    let itemListRenderer: ItemListRenderer<ItemType> | undefined;
    if (isLoadingItems) {
      itemListRenderer = loadingItemListRenderer<SubItemType, ItemType>({
        loadingTestId,
      });
    } else if (virtualized) {
      itemListRenderer = virtualizedSelectItemListRenderer<
        SubItemType,
        ItemType
      >({
        listRef: this.listRef,
        noResultsMenuItem,
      });
    }

    const hasSelection = Array.isArray(selected)
      ? selected.length
      : selected !== null;

    const showClearButton = clearable && isSelectorHovered && hasSelection;

    const clearButton = showClearButton ? (
      <AnchorButton
        className="sd-selector-clear"
        data-testid={clearTestId}
        disabled={disabled}
        icon={IconNames.CROSS}
        onClick={this.handleClear}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
      />
    ) : null;

    const selectorButton = (
      <AnchorButton
        className={classNames('sd-selector-button', buttonClassName, {
          'sd-selector-button--no-text': !buttonText,
        })}
        data-testid={testId}
        disabled={disabled}
        icon={
          buttonIcon ? (
            <Icon color={buttonIconColor} icon={buttonIcon} />
          ) : undefined
        }
        minimal={buttonMinimal}
        onClick={this.handleButtonClick}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        outlined={buttonOutlined}
        rightIcon={buttonRightIcon as IconName | MaybeElement}
      >
        {typeof buttonText === 'string' ? (
          <Text ellipsize>{buttonText}</Text>
        ) : (
          buttonText
        )}
      </AnchorButton>
    );

    const buttonGroup = (
      <ButtonGroup
        className={classNames('sd-selector-button-group', {
          'sd-selector-button-group__clearable': clearable,
          'sd-selector-button-group__with-clear': showClearButton,
        })}
      >
        {tooltipProps ? (
          // weird, but unless openOnTargetFocus === false, tooltip will open
          // when selector popover closes (may be because of `shouldReturnFocusOnClose`)
          // https://github.com/palantir/blueprint/issues/3514
          // eslint-disable-next-line react/jsx-props-no-spreading
          <Tooltip {...tooltipProps} openOnTargetFocus={false}>
            {selectorButton}
          </Tooltip>
        ) : (
          selectorButton
        )}
        {clearButton}
      </ButtonGroup>
    );

    return (
      <Select2<ItemType>
        className={classNames('sd-selector', className)}
        createNewItemFromQuery={
          creatable
            ? (query) => {
                const trimmedQuery = query.trim();
                // Note: createNewItemFromQuery needs to be defined by user if not using SelectItem!
                const fn = createNewItemFromQuery as NonNullable<
                  Select2Props<ItemType>['createNewItemFromQuery']
                >;
                return fn(trimmedQuery);
              }
            : undefined
        }
        createNewItemPosition="first"
        createNewItemRenderer={
          creatable
            ? createNewItemRenderer(items, createNewItemTestId)
            : undefined
        }
        disabled={disabled}
        filterable={filterable}
        inputProps={
          {
            placeholder: creatable ? filterOrCreateText : filterText,
            'data-testid': filterTestId,
            ...filterInputProps,
          } as any
        }
        itemListPredicate={itemListPredicate}
        itemListRenderer={itemListRenderer}
        itemRenderer={itemRenderer}
        items={sortedItems}
        itemsEqual={itemsEqual}
        noResults={noResultsMenuItem}
        onItemSelect={this.handleSelect}
        onQueryChange={(query) => {
          onQueryChange?.(query);
          this.setState({
            sortedItems: ItemSelector.getSortedItems(this.props),
          });
          window.requestAnimationFrame(() => {
            this.listRef.current?.scrollToItem(0, 'start');
          });
        }}
        popoverProps={{
          rootBoundary: 'document',
          position: PopoverPosition.BOTTOM,
          minimal: true,
          portalContainer:
            (process.env.NODE_ENV === 'test' &&
              document.getElementById('vet-bones-root')) ||
            // istanbul ignore next
            undefined,
          hasBackdrop: true,
          ...popoverProps,
          backdropProps: {
            ...popoverProps?.backdropProps,
            onClick: (event: React.MouseEvent<HTMLDivElement>) => {
              popoverProps?.backdropProps?.onClick?.(event);
              this.setState({ isOpen: false });
            },
          },
          isOpen,
          popoverClassName: classNames(
            'sd-popover-menu',
            popoverProps?.popoverClassName
          ),
        }}
        resetOnClose={resetOnClose}
        scrollToActiveItem={false}
      >
        {buttonGroup}
      </Select2>
    );
  }
}
